import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { FadeUp } from "@/components/Reveal";
import { Spark } from "@/components/Doodles";
import { api, formatApiErrorDetail } from "@/lib/api";

const TIME_OPTIONS = Array.from({ length: 17 }, (_, i) => {
    const h24 = 7 + i;
    const label = `${h24 % 12 === 0 ? 12 : h24 % 12}:00 ${h24 < 12 ? "AM" : "PM"}`;
    return { value: `${String(h24).padStart(2, "0")}:00`, label };
});
const todayIso = () => new Date().toISOString().slice(0, 10);
const maxIso = () => new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);

const StudioManage = () => {
    const { token } = useParams();
    const [booking, setBooking] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [cancelled, setCancelled] = useState(false);
    const [moving, setMoving] = useState(false);
    const [moveForm, setMoveForm] = useState({ date: todayIso(), start: "10:00" });

    useEffect(() => {
        api.get(`/studio/guest/${token}`)
            .then((r) => { setBooking(r.data); setMoveForm({ date: r.data.date, start: r.data.start }); })
            .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "That link doesn't match a reservation."));
    }, [token]);

    const cancel = async () => {
        setBusy(true);
        try {
            await api.post(`/studio/guest/${token}/cancel`);
            setCancelled(true);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
        setBusy(false);
    };

    const reschedule = async () => {
        setBusy(true);
        try {
            await api.post(`/studio/guest/${token}/reschedule`, moveForm);
            toast.success("Moved — new time locked in.");
            setMoving(false);
            const r = await api.get(`/studio/guest/${token}`);
            setBooking(r.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
        setBusy(false);
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-cream px-5 py-24">
            <Seo title="Manage Reservation" description="Manage your PCCH studio reservation." />
            <FadeUp className="w-full max-w-md">
                <div className="stamp-frame border-2 border-ink bg-paper p-8 text-center hard-shadow-lg">
                    <Spark className="mx-auto h-8 w-8 text-orange" />
                    <h1 className="mt-2 font-display text-3xl tracking-wide text-ink">YOUR RESERVATION</h1>
                    {error && <p data-testid="manage-error" className="mt-4 font-semibold text-brick">{error}</p>}
                    {!booking && !error && <p className="mt-4 font-hand text-2xl text-ink/60">Digging it out of the drawer…</p>}
                    {booking && (
                        <div data-testid="manage-booking" className="mt-5 text-left">
                            <ul className="space-y-2 text-sm font-semibold text-ink/80">
                                <li><span className="text-ink/50">Name:</span> {booking.user_name}</li>
                                <li data-testid="manage-when"><span className="text-ink/50">When:</span> {booking.date} at {booking.start}</li>
                                <li><span className="text-ink/50">Hours:</span> {booking.hours}</li>
                                {booking.purpose ? <li><span className="text-ink/50">Mission:</span> {booking.purpose}</li> : null}
                                <li>
                                    <span className="text-ink/50">Status:</span>{" "}
                                    <span className={booking.status === "confirmed" ? "text-forest" : "text-brick"}>
                                        {cancelled ? "cancelled" : booking.status === "confirmed" ? "confirmed" : booking.status.replace(/_/g, " ")}
                                    </span>
                                </li>
                            </ul>
                            {!cancelled && booking.status !== "cancelled" && (
                                <div className="mt-6 space-y-3">
                                    {moving ? (
                                        <div className="space-y-3 border-t border-dashed border-ink/30 pt-4">
                                            <label className="block text-sm font-semibold text-ink" htmlFor="guest-move-date">New date</label>
                                            <input id="guest-move-date" data-testid="guest-move-date" type="date" min={todayIso()} max={maxIso()} value={moveForm.date}
                                                onChange={(e) => setMoveForm({ ...moveForm, date: e.target.value })}
                                                className="w-full border-2 border-ink bg-cream px-3 py-2 text-ink" />
                                            <label className="block text-sm font-semibold text-ink" htmlFor="guest-move-start">New start time</label>
                                            <select id="guest-move-start" data-testid="guest-move-start" value={moveForm.start}
                                                onChange={(e) => setMoveForm({ ...moveForm, start: e.target.value })}
                                                className="w-full border-2 border-ink bg-cream px-3 py-2 text-ink">
                                                {TIME_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                            </select>
                                            <div className="flex gap-2">
                                                <button onClick={reschedule} disabled={busy} data-testid="manage-reschedule-save" className="btn-stamp flex-1 !bg-forest disabled:opacity-60">
                                                    {busy ? "Moving…" : "Move It"}
                                                </button>
                                                <button onClick={() => setMoving(false)} className="btn-stamp flex-1 !bg-cream !text-ink">Never Mind</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-3 sm:flex-row">
                                            <button onClick={() => setMoving(true)} data-testid="manage-reschedule-button" className="btn-stamp flex-1 !bg-mustard !text-ink">
                                                Reschedule
                                            </button>
                                            <button onClick={cancel} disabled={busy} data-testid="manage-cancel-button" className="btn-stamp flex-1 !bg-brick disabled:opacity-60">
                                                {busy ? "Releasing it…" : "Cancel This Reservation"}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {cancelled && (
                                <p data-testid="manage-cancelled" className="mt-5 border-2 border-dashed border-forest bg-cream px-4 py-3 font-hand text-xl text-forest">
                                    Released back into the wild. Hope to see you another time.
                                </p>
                            )}
                        </div>
                    )}
                    <Link to="/" data-testid="manage-home-link" className="mt-6 inline-block font-display text-sm tracking-wider text-forest underline decoration-mustard decoration-2 underline-offset-4 hover:text-brick">
                        Back to the Clubhouse
                    </Link>
                </div>
            </FadeUp>
        </div>
    );
};

export default StudioManage;
